import { useMemo, useState } from "react";
import { useNowTick } from "../hooks/useNowTick";
import { useRouteConfig } from "../settings/useRouteConfig";
import {
  listVehicleRuns,
  runDistanceKm,
  runDurationSec,
  type RunSource,
  type VehicleRun,
} from "../api/mockMileage";

// ── formatting helpers ───────────────────────────────────────────────────

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function fmtDuration(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

function fmtKm(km: number): string {
  return km.toFixed(km < 100 ? 2 : 1);
}

const SOURCE_LABEL: Record<RunSource, string> = {
  auto: "Auto-detected",
  driver: "Driver-initiated",
};

type SourceFilter = "all" | RunSource;
type StatusFilter = "all" | "in_progress" | "completed";

const SORTS = [
  { value: "recent", label: "Most recent" },
  { value: "km_desc", label: "Distance (high → low)" },
  { value: "duration_desc", label: "Duration (long → short)" },
] as const;
type SortKey = (typeof SORTS)[number]["value"];

export default function MileagePage() {
  // One shared 1-second tick drives every live cell on the page, so
  // in-progress runs count up without a timer per row.
  const now = useNowTick(1000);

  const [serviceDate, setServiceDate] = useState<string>(todayIso());
  const [busFilter, setBusFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [selected, setSelected] = useState<VehicleRun | null>(null);

  // Manually-measured per-route distances, edited under Settings.
  const [routeConfig] = useRouteConfig();

  // MOCK: swap for `api.listVehicleRuns({ service_date })` once the backend
  // exposes run distances. Regenerated only when the date changes — the live
  // numbers come from the tick, not from re-seeding the mock.
  const allRuns = useMemo(() => listVehicleRuns(serviceDate), [serviceDate]);

  const buses = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of allRuns) seen.set(r.vehicle_id, r.plate);
    return [...seen.entries()]
      .map(([id, plate]) => ({ id, plate }))
      .sort((a, b) => a.plate.localeCompare(b.plate));
  }, [allRuns]);

  const runs = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = allRuns.filter((r) => {
      if (busFilter !== "all" && r.vehicle_id !== busFilter) return false;
      if (sourceFilter !== "all" && r.source !== sourceFilter) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (q) {
        const hay = `${r.plate} ${r.driver_name} ${r.driver_employee_id} ${r.route_long_name}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const sorted = [...filtered];
    if (sort === "km_desc") {
      sorted.sort((a, b) => runDistanceKm(b, now) - runDistanceKm(a, now));
    } else if (sort === "duration_desc") {
      sorted.sort((a, b) => runDurationSec(b, now) - runDurationSec(a, now));
    }
    return sorted;
    // `now` deliberately excluded: re-sorting every second would make rows
    // jump under the operator's cursor. Sorting settles on the next filter
    // change, which is close enough for a live board.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRuns, busFilter, sourceFilter, statusFilter, search, sort]);

  const totals = useMemo(() => {
    let km = 0, live = 0, liveKm = 0, autoRuns = 0, durationSec = 0;
    for (const r of runs) {
      const d = runDistanceKm(r, now);
      km += d;
      durationSec += runDurationSec(r, now);
      if (r.status === "in_progress") { live++; liveKm += d; }
      if (r.source === "auto") autoRuns++;
    }
    const autoPct = runs.length ? Math.round((autoRuns / runs.length) * 100) : 0;
    const avgKm = runs.length ? km / runs.length : 0;
    const avgSpeed = durationSec > 0 ? km / (durationSec / 3600) : 0;
    return { km, live, liveKm, autoPct, autoRuns, avgKm, avgSpeed, count: runs.length };
  }, [runs, now]);

  // Payable kilometres = revenue km (trips x measured route length) + dead
  // mileage (depot->terminal and terminal->depot, once per bus per day).
  // Kept as separate line items because they scale differently: revenue km
  // with the trip count, dead mileage with the number of buses signed on.
  const payable = useMemo(() => {
    const byRoute = new Map<string, { trips: number; inProgress: number }>();
    // Which route each bus spent most of its day on — that route's depot
    // distances are the ones its deadhead is charged at.
    const busTrips = new Map<string, Map<string, number>>();

    for (const r of runs) {
      const row = byRoute.get(r.route_long_name) ?? { trips: 0, inProgress: 0 };
      row.trips++;
      if (r.status === "in_progress") row.inProgress++;
      byRoute.set(r.route_long_name, row);

      const perRoute = busTrips.get(r.plate) ?? new Map<string, number>();
      perRoute.set(r.route_long_name, (perRoute.get(r.route_long_name) ?? 0) + 1);
      busTrips.set(r.plate, perRoute);
    }

    const revenueRows = [...byRoute.entries()]
      .map(([route, v]) => {
        const cfg = routeConfig[route];
        return {
          route,
          trips: v.trips,
          inProgress: v.inProgress,
          lengthKm: cfg?.lengthKm ?? null,
          subtotal: cfg ? v.trips * cfg.lengthKm : null,
        };
      })
      .sort((a, b) => b.trips - a.trips);

    // One deadhead pair per bus per service date.
    const deadByRoute = new Map<string, { buses: number; outKm: number; inKm: number }>();
    for (const [, perRoute] of busTrips) {
      let primary = "";
      let best = -1;
      for (const [route, n] of perRoute) {
        if (n > best) { best = n; primary = route; }
      }
      const cfg = routeConfig[primary];
      if (!cfg) continue;
      const row = deadByRoute.get(primary) ?? { buses: 0, outKm: cfg.deadOutKm, inKm: cfg.deadInKm };
      row.buses++;
      deadByRoute.set(primary, row);
    }
    const deadRows = [...deadByRoute.entries()]
      .map(([route, v]) => ({
        route,
        buses: v.buses,
        outKm: v.outKm,
        inKm: v.inKm,
        subtotal: v.buses * (v.outKm + v.inKm),
      }))
      .sort((a, b) => b.subtotal - a.subtotal);

    const revenueKm = revenueRows.reduce((a, r) => a + (r.subtotal ?? 0), 0);
    const deadKm = deadRows.reduce((a, r) => a + r.subtotal, 0);
    const trips = revenueRows.reduce((a, r) => a + r.trips, 0);
    const buses = busTrips.size;
    const unpriced = revenueRows.filter((r) => r.lengthKm == null).length;

    return {
      revenueRows, deadRows, revenueKm, deadKm,
      totalKm: revenueKm + deadKm, trips, buses, unpriced,
    };
  }, [runs, routeConfig]);

  return (
    <div className="page mileage-page">
      <div className="mil-head">
        <div>
          <h2 style={{ margin: 0 }}>Run Kilometres</h2>
        </div>
        <div className="mil-head-right">
          <span className="mil-mock-tag">MOCK DATA</span>
          <span className="mil-clock mono">
            {new Date(now).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </span>
        </div>
      </div>

      {/* ── KPI tiles ────────────────────────────────────────────────── */}
      <div className="mil-kpis">
        <div className="mil-kpi">
          <span className="mil-kpi-label">Distance today</span>
          <span className="mil-kpi-value mono">
            {fmtKm(totals.km)}<span className="mil-kpi-unit">km</span>
          </span>
          <span className="mil-kpi-foot">
            {totals.count} run{totals.count === 1 ? "" : "s"} · {fmtKm(totals.avgKm)} km avg
          </span>
        </div>
        <div className="mil-kpi">
          <span className="mil-kpi-label">Accruing now</span>
          <span className="mil-kpi-value mono">
            {fmtKm(totals.liveKm)}<span className="mil-kpi-unit">km</span>
          </span>
          <span className="mil-kpi-foot">
            <span className="mil-live-dot" /> {totals.live} run{totals.live === 1 ? "" : "s"} in progress
          </span>
        </div>
        <div className="mil-kpi">
          <span className="mil-kpi-label">Auto-detected</span>
          <span className="mil-kpi-value mono">
            {totals.autoPct}<span className="mil-kpi-unit">%</span>
          </span>
          <span className="mil-kpi-foot">
            {totals.autoRuns} auto · {totals.count - totals.autoRuns} driver-started
          </span>
        </div>
        <div className="mil-kpi">
          <span className="mil-kpi-label">Avg speed</span>
          <span className="mil-kpi-value mono">
            {totals.avgSpeed.toFixed(1)}<span className="mil-kpi-unit">km/h</span>
          </span>
          <span className="mil-kpi-foot">incl. dwell time</span>
        </div>
      </div>

      {/* ── Filters ──────────────────────────────────────────────────── */}
      <div className="card mil-filters">
        <label>
          Service date
          <input type="date" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} />
        </label>
        <label>
          Bus
          <select value={busFilter} onChange={(e) => setBusFilter(e.target.value)}>
            <option value="all">All buses ({buses.length})</option>
            {buses.map((b) => <option key={b.id} value={b.id}>{b.plate}</option>)}
          </select>
        </label>
        <label>
          Started by
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}>
            <option value="all">All sources</option>
            <option value="auto">Auto-detected</option>
            <option value="driver">Driver-initiated</option>
          </select>
        </label>
        <label>
          Status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
            <option value="all">All</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
          </select>
        </label>
        <label>
          Sort
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label style={{ flex: 1, minWidth: 200 }}>
          Search
          <input
            type="text"
            placeholder="Plate, driver or route…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {/* ── Payable kilometres calculation ────────────────────────────── */}
      <div className="card mil-payable">
        <div className="mil-contract-head">
          <div>
            <h3 className="mil-contract-title">Payable kilometres</h3>
          </div>
          <div className="mil-kpi mil-kpi--hero mil-payable-total">
            <span className="mil-kpi-label">Payable total</span>
            <span className="mil-kpi-value mono">
              {fmtKm(payable.totalKm)}<span className="mil-kpi-unit">km</span>
            </span>
            <span className="mil-kpi-foot">
              {payable.trips} trip{payable.trips === 1 ? "" : "s"} · {payable.buses} bus
              {payable.buses === 1 ? "" : "es"}
            </span>
          </div>
        </div>

        {/* Revenue leg */}
        <div className="mil-calc-group">
          <div className="mil-calc-group-head">
            <span className="mil-calc-step">1</span>
            <h4>Revenue kilometres</h4>
            <span className="secondary-text">trips × measured route length</span>
          </div>
          <table className="data-table compact mil-calc-table">
            <thead>
              <tr>
                <th>Route</th>
                <th className="num">Trips</th>
                <th className="num">Route length</th>
                <th className="num">Working</th>
                <th className="num">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {payable.revenueRows.length === 0 && (
                <tr><td colSpan={5} className="empty">No trips match the current filters.</td></tr>
              )}
              {payable.revenueRows.map((row) => (
                <tr key={row.route}>
                  <td>
                    {row.route}
                    {row.inProgress > 0 && (
                      <span className="secondary-text"> · {row.inProgress} still running</span>
                    )}
                  </td>
                  <td className="num mono">{row.trips}</td>
                  <td className="num mono">
                    {row.lengthKm != null
                      ? `${row.lengthKm.toFixed(1)} km`
                      : <span className="mil-unpriced">not set</span>}
                  </td>
                  <td className="num mono mil-working">
                    {row.lengthKm != null ? `${row.trips} × ${row.lengthKm.toFixed(1)}` : "—"}
                  </td>
                  <td className="num mono mil-contract-sub">
                    {row.subtotal != null ? `${fmtKm(row.subtotal)} km` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="num">Revenue subtotal</td>
                <td className="num mono mil-contract-sub">{fmtKm(payable.revenueKm)} km</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Dead mileage leg */}
        <div className="mil-calc-group">
          <div className="mil-calc-group-head">
            <span className="mil-calc-step">2</span>
            <h4>Dead mileage</h4>
            <span className="secondary-text">
              buses × (depot → terminal + terminal → depot), once per bus per day
            </span>
          </div>
          <table className="data-table compact mil-calc-table">
            <thead>
              <tr>
                <th>Route</th>
                <th className="num">Buses</th>
                <th className="num">Depot → terminal</th>
                <th className="num">Terminal → depot</th>
                <th className="num">Working</th>
                <th className="num">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {payable.deadRows.length === 0 && (
                <tr><td colSpan={6} className="empty">No dead mileage to apply.</td></tr>
              )}
              {payable.deadRows.map((row) => (
                <tr key={row.route}>
                  <td>{row.route}</td>
                  <td className="num mono">{row.buses}</td>
                  <td className="num mono">{row.outKm.toFixed(1)} km</td>
                  <td className="num mono">{row.inKm.toFixed(1)} km</td>
                  <td className="num mono mil-working">
                    {row.buses} × ({row.outKm.toFixed(1)} + {row.inKm.toFixed(1)})
                  </td>
                  <td className="num mono mil-contract-sub">{fmtKm(row.subtotal)} km</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} className="num">Dead mileage subtotal</td>
                <td className="num mono mil-contract-sub">{fmtKm(payable.deadKm)} km</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Result */}
        <div className="mil-calc-result">
          <span className="mil-calc-step">3</span>
          <span className="mil-calc-formula mono">
            {fmtKm(payable.revenueKm)} km revenue + {fmtKm(payable.deadKm)} km dead mileage
          </span>
          <span className="mil-calc-equals">=</span>
          <span className="mil-calc-answer mono">{fmtKm(payable.totalKm)} km</span>
        </div>

        {payable.unpriced > 0 && (
          <div className="mil-contract-foot">
            <span className="mil-unpriced">
              {payable.unpriced} route{payable.unpriced === 1 ? " has" : "s have"} no measured
              distance configured and {payable.unpriced === 1 ? "is" : "are"} excluded from the
              total — set one under Settings → Measured route distances.
            </span>
          </div>
        )}
      </div>

      {/* ── Run log ──────────────────────────────────────────────────── */}
      <table className="data-table mil-table">
        <thead>
          <tr>
            <th className="mil-sn">#</th>
            <th>Status</th>
            <th>Vehicle / Driver</th>
            <th>Route</th>
            <th>Started by</th>
            <th>Start → End</th>
            <th>Duration</th>
            <th>Stops</th>
            <th className="num">Distance</th>
            <th className="num">Avg speed</th>
            <th className="num">Odometer</th>
          </tr>
        </thead>
        <tbody>
          {runs.length === 0 && (
            <tr><td colSpan={11} className="empty">No runs for {serviceDate}.</td></tr>
          )}
          {runs.map((r, i) => {
            const live = r.status === "in_progress";
            const km = runDistanceKm(r, now);
            const dur = runDurationSec(r, now);
            const pct = Math.round((r.stops_covered / r.stops_planned) * 100);
            return (
              <tr
                key={r.id}
                className={(live ? "mil-row--live" : "") + (selected?.id === r.id ? " is-selected" : "")}
                onClick={() => setSelected(r)}
                style={{ cursor: "pointer" }}
              >
                <td className="mil-sn mono">{i + 1}</td>
                <td>
                  <span className={"pill " + (live ? "sev-ok" : "sev-info")}>
                    {live && <span className="mil-live-dot" />}
                    {live ? "running" : "completed"}
                  </span>
                </td>
                <td>
                  <div className="primary-text mono">{r.plate}</div>
                  <div className="secondary-text">{r.driver_name} · {r.driver_employee_id}</div>
                </td>
                <td>
                  <span
                    className="route-badge"
                    style={{ background: `#${r.route_color}`, color: "#1b1b1b" }}
                  >
                    {r.route_short_name}
                  </span>
                  <div className="secondary-text" style={{ marginTop: 4 }}>{r.route_long_name}</div>
                </td>
                <td>
                  <span className={"mil-src mil-src--" + r.source}>
                    {SOURCE_LABEL[r.source]}
                  </span>
                </td>
                <td>
                  <div className="mono">{fmtTime(r.started_at)}</div>
                  <div className="secondary-text mono">→ {r.ended_at ? fmtTime(r.ended_at) : "live"}</div>
                </td>
                <td className="mono">{fmtDuration(dur)}</td>
                <td>
                  <div className="mono">{r.stops_covered} / {r.stops_planned}</div>
                  <div className="mil-bar"><div className="mil-bar-fill" style={{ width: `${pct}%` }} /></div>
                </td>
                <td className="num">
                  <span className={"mil-km mono" + (live ? " mil-km--live" : "")}>{fmtKm(km)}</span>
                  <span className="mil-km-unit">km</span>
                </td>
                <td className="num mono">{r.avg_speed_kph.toFixed(1)}</td>
                <td className="num mono secondary-text">
                  {(r.odo_start_km + km).toFixed(0)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {selected && <RunDrawer run={selected} now={now} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ── Detail drawer: per-leg breakdown of one run ──────────────────────────

function RunDrawer({ run, now, onClose }: { run: VehicleRun; now: number; onClose: () => void }) {
  const km = runDistanceKm(run, now);
  const dur = runDurationSec(run, now);
  const live = run.status === "in_progress";
  // Legs already reached contribute their full length; the leg in flight is
  // whatever is left over between completed legs and the live total.
  let walked = 0;

  return (
    <aside className="drawer">
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3>{run.plate} · {fmtTime(run.started_at)}</h3>
        <button className="btn ghost" type="button" onClick={onClose}>Close</button>
      </header>

      <dl className="kv-grid" style={{ marginBottom: 16 }}>
        <dt>Status</dt>
        <dd><span className={"pill " + (live ? "sev-ok" : "sev-info")}>{live ? "running" : "completed"}</span></dd>
        <dt>Route</dt>
        <dd>{run.route_short_name} — {run.route_long_name}</dd>
        <dt>Driver</dt>
        <dd>{run.driver_name} ({run.driver_employee_id})</dd>
        <dt>Started by</dt>
        <dd>
          <span className={"mil-src mil-src--" + run.source}>{SOURCE_LABEL[run.source]}</span>
          <div className="secondary-text" style={{ marginTop: 4 }}>
            {run.source === "auto"
              ? "Opened automatically when the bus crossed the depot-exit geofence."
              : "Opened by the driver from the Driver Console app."}
          </div>
        </dd>
        <dt>Distance</dt>
        <dd className="mono">{fmtKm(km)} km</dd>
        <dt>Duration</dt>
        <dd className="mono">{fmtDuration(dur)}</dd>
        <dt>Stops covered</dt>
        <dd className="mono">{run.stops_covered} of {run.stops_planned}</dd>
        <dt>Avg speed</dt>
        <dd className="mono">{run.avg_speed_kph.toFixed(1)} km/h</dd>
        <dt>Idle time</dt>
        <dd className="mono">{run.idle_minutes} min</dd>
        <dt>Odometer</dt>
        <dd className="mono">
          {run.odo_start_km.toLocaleString()} → {(run.odo_start_km + km).toFixed(0)} km
        </dd>
      </dl>

      <h4 style={{ margin: "0 0 8px" }}>Segment breakdown</h4>
      <table className="data-table compact" style={{ marginTop: 0 }}>
        <thead>
          <tr>
            <th>#</th>
            <th>Segment</th>
            <th className="num">Km</th>
            <th className="num">Cumulative</th>
            <th>Arrived</th>
            <th className="num">Dwell</th>
          </tr>
        </thead>
        <tbody>
          {run.legs.map((leg) => {
            const done = leg.arrived_at != null;
            if (done) walked += leg.km;
            const cumulative = done ? walked : null;
            return (
              <tr key={leg.seq} className={done ? "" : "is-pending"}>
                <td className="mono">{leg.seq}</td>
                <td>
                  {leg.from_stop} <span className="secondary-text">→</span> {leg.to_stop}
                </td>
                <td className="num mono">{leg.km.toFixed(1)}</td>
                <td className="num mono">{cumulative != null ? cumulative.toFixed(1) : "—"}</td>
                <td className="mono">{fmtTime(leg.arrived_at)}</td>
                <td className="num mono">{leg.dwell_seconds != null ? `${leg.dwell_seconds}s` : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </aside>
  );
}
